import { z } from 'zod'
import { objectToSnake } from 'ts-case-convert'
import { API, handleError } from './api'
import {
  garmentExtendedSchema,
  garmentHistorySchema,
  garmentOptionSchema,
  garmentPropertiesSchema,
  garmentSchema,
  uploadedImageSchema,
} from './schemas'
import { garmentMeasurementSchema } from './schemas/measurement'
import { cleanObject } from '../helpers'

//* ---------------- APIS ---------------- *//
export async function setHiddenGarment(rawParams: Record<string, unknown>) {
  try {
    const { id, ...params } = setHiddenGarmentParamsSchema.parse(rawParams)
    const response = await API.get(`garments/${id}/closet_visibility`, { params })

    return response
  } catch (error) {
    return handleError(error, 'Garment')
  }
}

export async function getGarment(id: number) {
  try {
    const response = await API.get(`garments/${id}`)
    const garment = garmentExtendedSchema.parse(response.data)
    return garment
  } catch (error) {
    return handleError(error, 'Garment')
  }
}

export async function getGarments(rawParams: Record<string, unknown>) {
  try {
    const params = baseGarmentIndexParamsSchema.parse(cleanObject(rawParams))
    const response = await API.get('garments', { params })
    const garments = z.array(garmentSchema).parse(response.data)
    return { garments, totalGarments: response.headers.total }
  } catch (error) {
    return handleError(error, 'Garments')
  }
}

export async function getGarmentsExtended(rawParams: Record<string, unknown>) {
  try {
    const params = garmentIndexExtendedParamsSchema.parse(cleanObject(rawParams))
    const response = await API.get('garments', { params: { ...params, extended: '1' } })
    const garments = garmentExtendedSchema.array().parse(response.data)
    return { garments, totalGarments: response.headers.total }
  } catch (error) {
    return handleError(error, 'Extended Garments')
  }
}

export async function updateGarment(garmentId: number, rawParams: Record<string, unknown>) {
  try {
    const params = updateGarmentParamsSchema.parse(rawParams)
    const response = await API.put(`garments/${garmentId}`, params)
    const garment = garmentExtendedSchema.parse(response.data)
    return garment
  } catch (error) {
    return handleError(error, 'Garment')
  }
}

// Garment sub resources

export async function getGarmentHistory(garmentId: number) {
  try {
    const response = await API.get(`garments/${garmentId}/history`)
    return garmentHistorySchema.array().parse(response.data)
  } catch (error) {
    return handleError(error, 'Garment History')
  }
}

export async function getGarmentMeasurements(id: number) {
  try {
    const response = await API.get(`garments/${id}/measurements`)
    const measurements = z.array(garmentMeasurementSchema).parse(response.data)
    return measurements
  } catch (error) {
    return handleError(error, 'Garment Measurements')
  }
}

export async function getGarmentOptions(id: number) {
  try {
    const response = await API.get(`garments/${id}/options`)
    const options = z.array(garmentOptionSchema).parse(response.data)
    return options
  } catch (error) {
    return handleError(error, 'Garment Options')
  }
}

// TODO cts; 2026-06-25; slow and might be more information than we truly want
// though as of today, the materials information we want is only on this endpoint
export async function getGarmentProperties(garmentId: number) {
  try {
    const response = await API.get(`garments/${garmentId}/properties`)
    return garmentPropertiesSchema.parse(response.data)
  } catch (error) {
    return handleError(error, 'Garment Properties')
  }
}

export async function getGarmentImages(id: number) {
  try {
    const response = await API.get(`garments/${id}/images`)
    return z.array(uploadedImageSchema).parse(response.data)
  } catch (error) {
    return handleError(error, 'Garment Images')
  }
}

//* ---------------- PARAMS ---------------- *//
const baseGarmentIndexParamsSchema = z.object({
  page: z.number().optional(),
  perPage: z.number().optional(),
  extended: z.enum(['0', '1']).optional(),
  garmentType: z.string().optional(),
  dealerId: z.number().optional(),
  customerId: z.number().optional(),
  orderStatusCode: z.string().optional(),
  delayStatusCode: z.string().optional(),
  dealerOrderId: z.number().optional(),
  orderStatusId: z.number().optional(),
  delayStatusId: z.number().optional(),
  fabricId: z.number().optional(),
  mwPrefix: z.string().optional(),
  hideIncomplete: z.coerce.boolean().optional(),
})
export const garmentIndexExtendedParamsSchema = baseGarmentIndexParamsSchema
  .omit({ extended: true })
  .transform(objectToSnake)

const baseSetHiddenGarmentParamsSchema = z.object({
  id: z.coerce.number(),
  isHidden: z.enum(['0', '1']),
})
export const setHiddenGarmentParamsSchema = baseSetHiddenGarmentParamsSchema.transform(objectToSnake)

export const updateGarmentParamsSchema = z
  .object({
    overnightShip: z.boolean().optional(),
  })
  .transform(objectToSnake)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type SetHiddenGarmentParamsType = z.infer<typeof baseSetHiddenGarmentParamsSchema>
  }
}
